import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, AlertTriangle, RefreshCw, Timer, Users } from 'lucide-react';
import { api } from '../../lib/api.js';
import type {
  TelemetryOverview,
  TelemetryRoute,
  TelemetryUsageRow,
  TelemetryWorkerJob,
} from '../../lib/api.js';
import { num, pct } from '../../lib/format.js';
import {
  Button,
  ErrorState,
  Metric,
  Panel,
  Segmented,
  SkeletonMetrics,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type SortDirection,
} from '../../ui/index.js';
import { ChartPalette, DataTable, HBar, PairedBars } from '../charts.js';
import { IssuesPanel } from './IssuesPanel.js';
import { formatMs } from './format.js';
import { WhoIsHere } from './WhoIsHere.js';

/**
 * The platform telemetry dashboard (platform_admin only, `platform.telemetry`).
 *
 * Two questions from one event stream, per the schema comment in
 * db/migrations/050_telemetry.sql: "is anybody using this" and "what is
 * broken". Everything below answers one of those two, and the Issues panel —
 * the actual worklist — sits in the middle because it is the reason anyone
 * comes back to this screen a second time.
 */

const WINDOWS = [
  { value: '1', label: '1h' },
  { value: '24', label: '24h' },
  { value: '168', label: '7d' },
  { value: '720', label: '30d' },
];

interface Queryish<T> {
  data?: T;
  isLoading: boolean;
  error: unknown;
}

export function TelemetryTab({ tenant }: { tenant: string }) {
  const qc = useQueryClient();
  const [hours, setHours] = useState('24');

  const overview = useQuery({
    queryKey: ['telemetry', 'overview', hours],
    queryFn: () => api.get<TelemetryOverview>(`/api/v1/telemetry/overview?hours=${hours}`),
    // The default 30s poll is right for a queue somebody is staring at; it is
    // wasted work against a dashboard that is open in a background tab all
    // day. Refresh below is the only thing that re-runs these.
    refetchInterval: false,
  });
  const routes = useQuery({
    queryKey: ['telemetry', 'routes', hours],
    queryFn: () => api.get<{ routes: TelemetryRoute[] }>(`/api/v1/telemetry/routes?hours=${hours}`),
    refetchInterval: false,
  });
  const usage = useQuery({
    queryKey: ['telemetry', 'usage', hours],
    queryFn: () => api.get<{ usage: TelemetryUsageRow[] }>(`/api/v1/telemetry/usage?hours=${hours}`),
    refetchInterval: false,
  });
  const worker = useQuery({
    queryKey: ['telemetry', 'worker', hours],
    queryFn: () => api.get<{ jobs: TelemetryWorkerJob[] }>(`/api/v1/telemetry/worker-health?hours=${hours}`),
    refetchInterval: false,
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ['telemetry'] });

  const o = overview.data;

  return (
    <div className="space-y-4">
      <ChartPalette />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Segmented
          value={hours}
          onChange={setHours}
          options={WINDOWS}
          size="sm"
          aria-label="Time window"
          className="max-w-[16rem]"
        />
        <Button size="sm" variant="secondary" onClick={refresh}>
          <RefreshCw aria-hidden className="h-3.5 w-3.5" />
          Refresh
        </Button>
      </div>

      {overview.error && <ErrorState error={overview.error} />}
      {!o && !overview.error && <SkeletonMetrics />}
      {o && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Metric
            label="Requests"
            value={num(o.requests)}
            icon={<Activity />}
            hint={`${num(o.activeUsers)} active ${o.activeUsers === 1 ? 'person' : 'people'}`}
          />
          <Metric
            label="Error rate"
            value={pct(o.errorRate, 1)}
            tone={o.errorRate > 0.05 ? 'danger' : o.errorRate > 0.01 ? 'warn' : 'ok'}
            icon={<AlertTriangle />}
            hint={`${num(o.errors)} error${o.errors === 1 ? '' : 's'}`}
          />
          <Metric
            label="p95 latency"
            value={formatMs(o.p95Ms)}
            icon={<Timer />}
            hint={`p50 ${formatMs(o.p50Ms)}`}
          />
          <Metric
            label="Active users"
            value={num(o.activeUsers)}
            icon={<Users />}
            hint="Distinct signed-in people, this window"
          />
        </div>
      )}

      {/* UNDER THE NUMBERS, not above them.
          The metrics are what this screen is for and what somebody's eye
          should land on; who happens to be online is context for reading
          them. It was at the top and pushed the whole dashboard down. */}
      <WhoIsHere tenant={tenant} />

      {o && (
        <Panel
          title="Requests and errors"
          subtitle="By day — volume and defects from the same rows, so one never gets read without the other"
        >
          {o.byDay.length === 0 ? (
            <p className="py-6 text-center text-xs text-ink-tertiary">
              No requests recorded in this window yet.
            </p>
          ) : (
            <PairedBars
              rows={o.byDay.map((d) => ({ label: d.day, a: d.requests, b: d.errors }))}
              labelA="Requests"
              labelB="Errors"
              format={(v) => num(v)}
            />
          )}
        </Panel>
      )}

      <IssuesPanel hours={hours} />

      <div className="grid gap-4 xl:grid-cols-2">
        <RoutesPanel query={routes} />
        <UsagePanel query={usage} />
      </div>

      <WorkerPanel query={worker} />
    </div>
  );
}

type RouteSortKey = 'calls' | 'p95Ms' | 'errorRate';

function RoutesPanel({ query }: { query: Queryish<{ routes: TelemetryRoute[] }> }) {
  const [sortKey, setSortKey] = useState<RouteSortKey>('calls');
  const [dir, setDir] = useState<SortDirection>('desc');

  const toggle = (key: RouteSortKey) => {
    if (key === sortKey) setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setDir('desc');
    }
  };

  const rows = [...(query.data?.routes ?? [])].sort((a, b) => {
    const mul = dir === 'asc' ? 1 : -1;
    return (a[sortKey] - b[sortKey]) * mul;
  });

  return (
    <Panel
      title="Slowest and most-failing routes"
      subtitle="Click a column to sort — calls, p95, or error rate"
      flush={rows.length > 0}
    >
      {query.isLoading && <Spinner />}
      {query.error ? <ErrorState error={query.error} /> : null}
      {query.data && rows.length === 0 && (
        <p className="py-6 text-center text-xs text-ink-tertiary">
          No API calls recorded in this window.
        </p>
      )}
      {rows.length > 0 && (
        <Table density="compact">
          <THead>
            <TR>
              <TH>Route</TH>
              <TH numeric sortable direction={sortKey === 'calls' ? dir : null} onSort={() => toggle('calls')}>
                Calls
              </TH>
              <TH numeric sortable direction={sortKey === 'p95Ms' ? dir : null} onSort={() => toggle('p95Ms')}>
                p95
              </TH>
              <TH
                numeric
                sortable
                direction={sortKey === 'errorRate' ? dir : null}
                onSort={() => toggle('errorRate')}
              >
                Errors
              </TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((r) => (
              <TR key={r.name}>
                <TD>
                  <code className="font-mono text-2xs text-ink-secondary">{r.name}</code>
                </TD>
                <TD numeric>{num(r.calls)}</TD>
                <TD numeric>{formatMs(r.p95Ms)}</TD>
                <TD numeric className={r.errorRate > 0.05 ? 'font-medium text-danger' : undefined}>
                  {pct(r.errorRate, 1)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Panel>
  );
}

function UsagePanel({ query }: { query: Queryish<{ usage: TelemetryUsageRow[] }> }) {
  const rows = query.data?.usage ?? [];
  return (
    <Panel title="Usage" subtitle="What people actually use, this window — not what shipped">
      {query.isLoading && <Spinner />}
      {query.error ? <ErrorState error={query.error} /> : null}
      {query.data && rows.length === 0 && (
        <p className="py-6 text-center text-xs text-ink-tertiary">
          No usage events recorded in this window.
        </p>
      )}
      {rows.length > 0 && (
        <HBar
          rows={[...rows]
            .sort((a, b) => b.count - a.count)
            .map((r) => ({
              label: r.name,
              value: r.count,
              hint: `${r.name} (${r.kind}): ${num(r.count)}`,
            }))}
          valueLabel={(v) => num(v)}
        />
      )}
    </Panel>
  );
}

function WorkerPanel({ query }: { query: Queryish<{ jobs: TelemetryWorkerJob[] }> }) {
  const rows = query.data?.jobs ?? [];
  return (
    <Panel title="Worker health" subtitle="Runs and failures per loop, this window">
      {query.isLoading && <Spinner />}
      {query.error ? <ErrorState error={query.error} /> : null}
      {query.data && rows.length === 0 && (
        <p className="py-6 text-center text-xs text-ink-tertiary">
          No worker jobs recorded in this window.
        </p>
      )}
      {rows.length > 0 && (
        <DataTable
          head={['Job', 'Runs', 'Failures', 'p95']}
          rows={rows.map((r) => [
            r.name,
            num(r.runs),
            r.failures > 0 ? (
              <span key="f" className="font-medium text-danger">
                {num(r.failures)}
              </span>
            ) : (
              num(r.failures)
            ),
            formatMs(r.p95Ms),
          ])}
        />
      )}
    </Panel>
  );
}
